"use client";

import { useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { SlopeGraph } from "@/components/tools/math/slope-graph";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { decimalMarkOf } from "@/lib/tool-kit/math/locale";
import { formatRadicalFraction } from "@/lib/tool-kit/math/radical";
import {
  addR,
  formatStyled,
  mulR,
  type NumberStyle,
  parseRational,
  type Rational,
  signR,
  toNumberR,
} from "@/lib/tool-kit/math/rational";
import {
  computeSlope,
  pointSlopeText,
  type SlopeResult,
  slopeInterceptText,
  standardFormText,
} from "@/lib/tool-kit/math/slope";

/** English strings. A translated content file overrides these through `ui`. */
export const SLOPE_CALCULATOR_STRINGS = {
  point1: "Point 1 (x₁, y₁)",
  point2: "Point 2 (x₂, y₂)",
  x1: "x₁",
  y1: "y₁",
  x2: "x₂",
  y2: "y₂",
  coordinateHint: "Whole numbers, decimals or fractions such as 1/2.",
  results: "Results",
  slope: "Slope (m)",
  slopeDecimal: "Slope as a decimal",
  angle: "Angle of inclination",
  distance: "Distance between the points",
  midpoint: "Midpoint",
  yIntercept: "y-intercept (b)",
  undefinedSlope: "Undefined",
  none: "None",
  degrees: "{value}°",
  equationsHeading: "Equation of the line",
  slopeIntercept: "Slope-intercept form",
  pointSlope: "Point-slope form",
  standardForm: "Standard form",
  notForVertical: "Not defined for a vertical line",
  copyEquations: "Copy equations",
  copied: "Copied!",
  workingHeading: "The working",
  slopeWorking:
    "m = (y₂ − y₁) / (x₂ − x₁) = ({y2} − {y1}) / ({x2} − {x1}) = {rise} / {run} = {slope}",
  verticalWorking:
    "x₂ − x₁ = 0, so the slope is undefined: the line is vertical, with equation x = {x}.",
  horizontalWorking:
    "y₂ − y₁ = 0, so the slope is 0: the line is horizontal, with equation y = {y}.",
  angleWorking: "θ = arctan(m) = arctan({slope}) ≈ {angle}°",
  angleNegativeWorking:
    "θ = arctan({slope}) ≈ {arctan}°; measured counterclockwise from the positive x-axis, θ = 180° + ({arctan}°) ≈ {angle}°",
  distanceWorking:
    "d = √((x₂ − x₁)² + (y₂ − y₁)²) = √({run}² + {rise}²) = √{sum} = {exact}",
  midpointWorking:
    "M = ((x₁ + x₂) / 2, (y₁ + y₂) / 2) = (({x1} + {x2}) / 2, ({y1} + {y2}) / 2) = {midpoint}",
  interceptWorking: "b = y₁ − m × x₁ = {y1} − {m} × {x1} = {b}",
  parallel:
    "A parallel line has the same slope, {m}; a perpendicular line has slope {perpendicular}, the negative reciprocal.",
  parallelVertical:
    "Lines parallel to it are vertical too, and perpendicular lines are horizontal, with slope 0.",
  parallelHorizontal:
    "Lines parallel to it have slope 0, and perpendicular lines are vertical, with undefined slope.",
  graphHeading: "Graph",
  graphTitle: "The line through {p1} and {p2}, with the rise and run drawn",
  point: "({x}, {y})",
  riseLabel: "rise {value}",
  runLabel: "run {value}",
  downloadPng: "Download graph (PNG)",
  samePoint:
    "The two points are the same, so they do not determine a line. Enter two different points.",
  enterPoints: "Enter both coordinates of both points.",
  invalid:
    "Enter each coordinate as a number or a fraction, such as 3, −1.5 or 2/3.",
} as const;

type Strings = typeof SLOPE_CALCULATOR_STRINGS;
type Format = (value: number, options?: Intl.NumberFormatOptions) => string;

const DECIMAL_INPUT = /[.,]/;

function short(fmt: Format, value: number): string {
  return fmt(value, { maximumFractionDigits: 4 });
}

const NEEDS_BRACKETS = /^−|\//;

/** "(−2)" and "(1/2)" where a number is substituted next to an operator. */
function bracket(text: string): string {
  return NEEDS_BRACKETS.test(text) ? `(${text})` : text;
}

function Working({
  result,
  strings,
  style,
}: {
  result: SlopeResult;
  strings: Strings;
  style: NumberStyle;
}) {
  const fmt = useToolNumber();
  const mark = decimalMarkOf(fmt);
  const v = (value: Rational) => formatStyled(value, style, mark);
  // Substituted values: one that follows an operator is bracketed when negative or a
  // fraction, as on paper; the first term of each expression is left bare.
  const raw = {
    x1: v(result.p1.x),
    y1: v(result.p1.y),
    x2: v(result.p2.x),
    y2: v(result.p2.y),
  };
  const sum = addR(mulR(result.dx, result.dx), mulR(result.dy, result.dy));
  const exactDistance = formatRadicalFraction(result.distance);
  const irrational = result.distance.radicand !== BigInt(1);
  const angle = fmt(result.angleDegrees, { maximumFractionDigits: 2 });
  let slopeLine = t(strings.verticalWorking, { x: v(result.p1.x) });
  if (result.kind === "horizontal") {
    slopeLine = t(strings.horizontalWorking, { y: v(result.p1.y) });
  } else if (result.slope !== null) {
    slopeLine = t(strings.slopeWorking, {
      y2: raw.y2,
      y1: bracket(raw.y1),
      x2: raw.x2,
      x1: bracket(raw.x1),
      rise: v(result.dy),
      run: v(result.dx),
      slope: v(result.slope),
    });
  }
  let angleLine = "";
  if (result.slope !== null && result.arctanDegrees !== null) {
    const values = {
      slope: v(result.slope),
      angle,
      arctan: fmt(result.arctanDegrees, { maximumFractionDigits: 2 }),
    };
    angleLine = t(
      signR(result.slope) < 0
        ? strings.angleNegativeWorking
        : strings.angleWorking,
      values
    );
  }
  return (
    <div className="space-y-1 rounded-lg border border-border bg-muted/30 p-4 font-mono text-muted-foreground text-sm leading-relaxed">
      <p>{slopeLine}</p>
      {angleLine === "" ? null : <p>{angleLine}</p>}
      <p>
        {t(strings.distanceWorking, {
          run: bracket(v(result.dx)),
          rise: bracket(v(result.dy)),
          sum: sum.den === BigInt(1) ? v(sum) : `(${v(sum)})`,
          exact: exactDistance,
        })}
        {irrational ? ` ≈ ${short(fmt, result.distanceDecimal)}` : null}
      </p>
      <p>
        {t(strings.midpointWorking, {
          x1: raw.x1,
          x2: bracket(raw.x2),
          y1: raw.y1,
          y2: bracket(raw.y2),
          midpoint: t(strings.point, {
            x: v(result.midpoint.x),
            y: v(result.midpoint.y),
          }),
        })}
      </p>
      {result.intercept === null ? null : (
        <p>
          {t(strings.interceptWorking, {
            y1: raw.y1,
            x1: bracket(raw.x1),
            m: bracket(v(result.slope ?? result.dy)),
            b: v(result.intercept),
          })}
        </p>
      )}
    </div>
  );
}

function Results({
  result,
  strings,
  style,
}: {
  result: SlopeResult;
  strings: Strings;
  style: NumberStyle;
}) {
  const fmt = useToolNumber();
  const mark = decimalMarkOf(fmt);
  const v = (value: Rational) => formatStyled(value, style, mark);
  const slopeIntercept = slopeInterceptText(result, style, mark);
  const pointSlope = pointSlopeText(result, style, mark);
  const standard = standardFormText(result.standard);
  const equations = [slopeIntercept, pointSlope, standard].filter(
    (e): e is string => e !== null
  );
  let related: string = strings.parallelVertical;
  if (result.kind === "horizontal") {
    related = strings.parallelHorizontal;
  } else if (result.slope !== null && result.perpendicular !== null) {
    related = t(strings.parallel, {
      m: v(result.slope),
      perpendicular: v(result.perpendicular),
    });
  }
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.slope}
          value={
            result.slope === null ? strings.undefinedSlope : v(result.slope)
          }
        />
        <StatCard
          label={strings.slopeDecimal}
          value={
            result.slope === null
              ? strings.undefinedSlope
              : short(fmt, toNumberR(result.slope))
          }
        />
        <StatCard
          label={strings.angle}
          value={t(strings.degrees, {
            value: fmt(result.angleDegrees, { maximumFractionDigits: 2 }),
          })}
        />
        <StatCard
          label={strings.distance}
          value={formatRadicalFraction(result.distance)}
        />
        <StatCard
          label={strings.midpoint}
          value={t(strings.point, {
            x: v(result.midpoint.x),
            y: v(result.midpoint.y),
          })}
        />
        <StatCard
          label={strings.yIntercept}
          value={result.intercept === null ? strings.none : v(result.intercept)}
        />
      </StatGrid>
      <h3 className={WIDGET_H3}>{strings.equationsHeading}</h3>
      <div className={`${RESULT_CARD} overflow-x-auto`}>
        <table className="w-full border-collapse text-left text-sm">
          <tbody>
            {[
              { label: strings.slopeIntercept, value: slopeIntercept },
              {
                label: strings.pointSlope,
                value: pointSlope ?? strings.notForVertical,
              },
              { label: strings.standardForm, value: standard },
            ].map((row) => (
              <tr key={row.label}>
                <th
                  className="py-1 pr-4 align-top font-normal text-muted-foreground"
                  scope="row"
                >
                  {row.label}
                </th>
                <td className="py-1 font-mono text-foreground">{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyEquations}
        small
        text={equations.join("\n")}
      />
      <p className="text-muted-foreground text-sm">{related}</p>
      <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
      <Working result={result} strings={strings} style={style} />
      <h3 className={WIDGET_H3}>{strings.graphHeading}</h3>
      <SlopeGraph result={result} strings={strings} style={style} />
    </>
  );
}

function Outcome({ texts, strings }: { texts: string[]; strings: Strings }) {
  if (texts.some((text) => text.trim() === "")) {
    return <Notice>{strings.enterPoints}</Notice>;
  }
  const values = texts.map(parseRational);
  if (values.some((value) => value === null)) {
    return <ErrorNotice>{strings.invalid}</ErrorNotice>;
  }
  const [x1, y1, x2, y2] = values as Rational[];
  const result = computeSlope({ x: x1, y: y1 }, { x: x2, y: y2 });
  if (result === null) {
    return <ErrorNotice>{strings.samePoint}</ErrorNotice>;
  }
  const style: NumberStyle = texts.some((text) => DECIMAL_INPUT.test(text))
    ? "decimal"
    : "fraction";
  return <Results result={result} strings={strings} style={style} />;
}

export default function SlopeCalculatorWidget() {
  const strings = useToolUi(SLOPE_CALCULATOR_STRINGS);
  const [x1, setX1] = useState("1");
  const [y1, setY1] = useState("2");
  const [x2, setX2] = useState("4");
  const [y2, setY2] = useState("6");
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <fieldset className="space-y-3 rounded-lg border border-border p-4">
          <legend className="px-1 font-medium text-foreground text-sm">
            {strings.point1}
          </legend>
          <div className="grid grid-cols-2 gap-3">
            <TextField
              id="sl-x1"
              label={strings.x1}
              mono
              onChange={setX1}
              value={x1}
            />
            <TextField
              id="sl-y1"
              label={strings.y1}
              mono
              onChange={setY1}
              value={y1}
            />
          </div>
        </fieldset>
        <fieldset className="space-y-3 rounded-lg border border-border p-4">
          <legend className="px-1 font-medium text-foreground text-sm">
            {strings.point2}
          </legend>
          <div className="grid grid-cols-2 gap-3">
            <TextField
              id="sl-x2"
              label={strings.x2}
              mono
              onChange={setX2}
              value={x2}
            />
            <TextField
              id="sl-y2"
              label={strings.y2}
              mono
              onChange={setY2}
              value={y2}
            />
          </div>
        </fieldset>
      </div>
      <p className="text-muted-foreground text-xs">{strings.coordinateHint}</p>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome strings={strings} texts={[x1, y1, x2, y2]} />
      </section>
    </>
  );
}
